/**
 * Le canal Telegram, vu du parent : est-ce que ça marche, et sinon quoi faire.
 *
 * **Cet écran existe à cause d'un silence de neuf jours.** `notifier` était
 * complet le 26 août 2026 et n'a rien envoyé jusqu'au 3 septembre : personne
 * n'était lié, et rien nulle part ne le disait. Un système de notification qui
 * n'envoie rien ressemble trait pour trait à un système de notification qui n'a
 * rien à dire — sauf ici, où la différence est écrite en toutes lettres.
 *
 * Trois états, pas deux, parce qu'ils appellent trois gestes différents :
 * le bot n'est pas configuré (affaire d'exploitation), il l'est mais cette
 * personne n'est pas liée (deux minutes sur un téléphone), tout est en place
 * (on peut alors vérifier pour de bon en s'envoyant un message).
 *
 * Le lien est **personnel et à usage unique** : il vaut quinze minutes, il lie
 * la conversation qui l'ouvre, et il ne peut lier que le parent connecté. Le
 * second parent ouvre le sien depuis son propre compte — pas celui-ci.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { EtatDuCanal, LienDeLiaison } from "@/api/schema";
import { Button, Card, ErrorNote, Loading, Screen } from "@/ui/primitives";

export function NotificationsScreen() {
  const [etat, setEtat] = useState<EtatDuCanal | null>(null);
  const [lien, setLien] = useState<LienDeLiaison | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    try {
      setEtat(await call("etatDuCanalTelegram"));
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const faire = async (action: () => Promise<void>) => {
    setOccupe(true);
    setErreur(null);
    setMessage(null);
    try {
      await action();
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : "Quelque chose n'a pas marché.");
    } finally {
      setOccupe(false);
    }
  };

  if (erreur && !etat) return <ErrorNote>{erreur}</ErrorNote>;
  if (!etat) return <Loading />;

  return (
    <Screen
      title="Notifications"
      lead="Les alertes et le récapitulatif du soir passent par Telegram, et par personne d'autre : les enfants n'y ont pas de compte."
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}

      {!etat.configure ? (
        <Card title="Le bot n'est pas configuré" tone="attention">
          <p>
            Cette installation n'a pas de jeton de bot. Rien ne peut partir, et il n'y a rien à
            faire depuis cet écran : c'est un réglage du serveur.
          </p>
        </Card>
      ) : etat.lie ? (
        <Card title="Vous êtes lié" tone="good">
          <p className="muted">
            Vous recevez les alertes de sécurité, les demandes de validation et le récapitulatif de
            21 h. Rien de ce que les enfants écrivent ne passe par là, sauf une alerte urgente.
          </p>
          <div className="list__actions">
            <Button
              variant="secondary"
              disabled={occupe}
              onClick={() =>
                void faire(async () => {
                  await call("envoyerUnTestTelegram");
                  setMessage("Message envoyé. S'il n'arrive pas, la conversation a été bloquée côté Telegram.");
                })
              }
            >
              M'envoyer un message de test
            </Button>
          </div>
          {message ? <p className="notice">{message}</p> : null}
        </Card>
      ) : (
        <Card title="Vous n'êtes pas encore lié" tone="attention">
          <p>
            Tant que cette étape n'est pas faite, <strong>vous ne recevez rien</strong> — pas même
            une alerte urgente.
          </p>
          <ol className="list">
            <li className="list__item">Demandez votre lien ci-dessous.</li>
            <li className="list__item">Ouvrez-le : Telegram démarre la conversation avec le bot.</li>
            <li className="list__item">Le bot répond « c'est lié ». C'est fini.</li>
          </ol>
          <div className="list__actions">
            <Button
              disabled={occupe}
              onClick={() =>
                void faire(async () => {
                  setLien(await call("emettreLeLienTelegram"));
                })
              }
            >
              Générer mon lien
            </Button>
          </div>

          {lien ? (
            <div className="notice">
              {lien.lien ? (
                <p>
                  <a href={lien.lien} target="_blank" rel="noreferrer">
                    Ouvrir Telegram et lier cette conversation
                  </a>
                </p>
              ) : null}
              <p className="muted small">
                Ou, dans la conversation avec le bot, tapez : <code>/start {lien.jeton}</code>
              </p>
              <p className="muted small">
                Valable quinze minutes, une seule fois. Passé ce délai, regénérez-en un — cela ne
                coûte rien et remplace le précédent.
              </p>
            </div>
          ) : null}
        </Card>
      )}

      <Card title="Ce qui arrive par ce canal">
        <ul className="list">
          <li className="list__item">Un appareil adopté par un enfant, tout de suite.</li>
          <li className="list__item">Un exercice à valider de votre main.</li>
          <li className="list__item">Le récapitulatif de la journée, à 21 h.</li>
          <li className="list__item">
            Une alerte urgente, qui ignore l'heure, le plafond quotidien et le regroupement.
          </li>
        </ul>
        <p className="muted small">
          Chaque parent se lie depuis son propre compte : un lien généré ici ne peut lier que vous.
        </p>
      </Card>
    </Screen>
  );
}
